import React, { memo, Suspense, useMemo } from 'react'
import styled from 'styled-components'
import AlertIcon from '~/components/icons/ui/16px_alert.svg'
import { AttendantData, DateRange } from './types/agentReport.types'
import { useLazyComponents } from './hooks/useLazyComponents'
import VisibleRender from '~/components/visibleRender'
import { MapCenterProvider } from './hooks/mapCenterContext'
import { Skeleton } from '~/components/skeletonLoading'
import theme from '~/design'
import isEqual from 'fast-deep-equal/react'

const ComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 10px;
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  width: 100%;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;

    > * {
      grid-column: span 1 !important;
      max-width: 100%;
    }
  }
`

interface AgentReportContentProps {
  agent: AttendantData | AttendantData[]
  dateRange: DateRange
  google: any
  apiKey: string
  validAttendances: any[]
  isLoadingAttendances: boolean | boolean[]
}

interface ReportSectionProps {
  isComparisonMode: boolean
  mainAgent: AttendantData
  comparisonAgent: AttendantData | null
  dependencies: any[]
  fallback: React.ReactNode
  render: (agent: AttendantData, isCompared?: boolean) => React.ReactNode
  showTitle?: boolean
  useVisibleRender?: boolean
  style?: React.CSSProperties
}

const ReportSection = ({
  isComparisonMode,
  mainAgent,
  comparisonAgent,
  dependencies,
  fallback,
  render,
  useVisibleRender = true,
  style,
}: ReportSectionProps) => {
  const renderContent = (agentItem: AttendantData, isCompared: boolean) => {
    const content = useVisibleRender ? (
      <VisibleRender dependencies={dependencies} loading={fallback} style={style}>
        <Suspense fallback={fallback}>{render(agentItem, isCompared)}</Suspense>
      </VisibleRender>
    ) : (
      <Suspense fallback={fallback}>{render(agentItem, isCompared)}</Suspense>
    )

    return <>{content}</>
  }

  if (isComparisonMode && comparisonAgent) {
    return (
      <>
        {renderContent(mainAgent, false)}
        {renderContent(comparisonAgent, true)}
      </>
    )
  }

  return renderContent(mainAgent, false)
}

function AgentReportContent({
  agent,
  dateRange,
  google,
  apiKey,
  validAttendances,
  isLoadingAttendances,
}: AgentReportContentProps) {
  const {
    AgentReportMap,
    AgentReportAttendancesMap,
    AgentReportProductionChart,
    AgentReportSize,
    AgentReportHistogram,
    AgentReportEconomicActivityList,
    AgentReportIsBranch,
  } = useLazyComponents()

  const isComparisonMode = Array.isArray(agent)
  const mainAgent = isComparisonMode ? agent[0] : agent
  const comparisonAgent = isComparisonMode ? agent[1] : null

  const isComparisonModeValidAttendances = Array.isArray(validAttendances)
  const mainAgentValidAttendances = isComparisonModeValidAttendances
    ? validAttendances[0]
    : validAttendances[0]
  const comparisonAgentValidAttendances = isComparisonModeValidAttendances
    ? validAttendances[1]
    : null

  const getAttendances = (agentItem: AttendantData) => {
    return agentItem === mainAgent
      ? mainAgentValidAttendances
      : comparisonAgentValidAttendances || []
  }

  const getLoading = (agentItem: AttendantData) => {
    if (!Array.isArray(isLoadingAttendances)) return isLoadingAttendances
    return agentItem === mainAgent ? isLoadingAttendances[0] : isLoadingAttendances[1]
  }

  if (!dateRange.startDate || !dateRange.endDate) {
    return (
      <ComponentWrapper>
        <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
          <div className='icon'>
            <AlertIcon fill={theme.colors.mediumGrey} width={48} height={48} />
          </div>
          <p>Selecione uma data de início e uma data final para visualizar as informações.</p>
        </div>
      </ComponentWrapper>
    )
  }

  const dependeciesAgent = useMemo(() => [dateRange, comparisonAgent], [dateRange, comparisonAgent])

  const agents = useMemo(
    () => (isComparisonMode && comparisonAgent ? [mainAgent, comparisonAgent] : [mainAgent]),
    [mainAgent, comparisonAgent, isComparisonMode],
  )

  if (!getLoading(mainAgent) && mainAgentValidAttendances.length === 0) {
    return (
      <ComponentWrapper>
        <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
          <div className='icon'>
            <AlertIcon fill={theme.colors.mediumGrey} width={48} height={48} />
          </div>
          <p>Não há dados para o agente selecionado no período escolhido.</p>
        </div>
      </ComponentWrapper>
    )
  }

  return (
    <MapCenterProvider>
      <GridContainer>
        <AgentReportMap
          agents={agents}
          google={google}
          startDate={dateRange.startDate ?? ''}
          endDate={dateRange.endDate ?? ''}
          validAttendancesArray={
            isComparisonMode && comparisonAgent
              ? [getAttendances(mainAgent), getAttendances(comparisonAgent)]
              : [getAttendances(mainAgent)]
          }
          isLoading={
            !!getLoading(mainAgent) || !!(comparisonAgent && !!getLoading(comparisonAgent))
          }
        />

        <ReportSection
          isComparisonMode={isComparisonMode}
          mainAgent={mainAgent}
          comparisonAgent={comparisonAgent}
          dependencies={dependeciesAgent}
          fallback={<Skeleton style={{ gridColumn: 'span 3' }} height='640px' />}
          useVisibleRender={false}
          render={(agentItem, isCompared) => (
            <AgentReportAttendancesMap
              agent={agentItem}
              apiKey={apiKey}
              validAttendances={getAttendances(agentItem)}
              isLoading={!!getLoading(agentItem)}
              isCompared={isCompared}
              title={isComparisonMode ? `Mapa de atendimentos - ${agentItem.name}` : undefined}
            />
          )}
        />

        <ReportSection
          isComparisonMode={isComparisonMode}
          mainAgent={mainAgent}
          comparisonAgent={comparisonAgent}
          dependencies={dependeciesAgent}
          fallback={<Skeleton height='330px' />}
          style={isComparisonMode ? { gridColumn: 'span 6' } : { gridColumn: 'span 6' }}
          render={(agentItem, isCompared) => (
            <AgentReportProductionChart
              isComparisonMode={isComparisonMode}
              isCompared={isCompared}
              attendant={agentItem}
              startDate={dateRange.startDate ?? ''}
              endDate={dateRange.endDate ?? ''}
              title={isComparisonMode ? `Gráfico de produtividade - ${agentItem.name}` : undefined}
            />
          )}
        />

        <VisibleRender
          dependencies={dependeciesAgent}
          loading={<Skeleton height='530px' />}
          style={isComparisonMode ? { gridColumn: 'span 6' } : { gridColumn: 'span 3' }}
        >
          <Suspense fallback={<Skeleton height='530px' />}>
            <AgentReportSize
              agents={agents}
              validAttendances={
                isComparisonMode && comparisonAgent
                  ? [getAttendances(mainAgent), getAttendances(comparisonAgent)]
                  : [getAttendances(mainAgent)]
              }
              isLoading={
                !!getLoading(mainAgent) || !!(comparisonAgent && !!getLoading(comparisonAgent))
              }
            />
          </Suspense>
        </VisibleRender>

        <VisibleRender
          dependencies={dependeciesAgent}
          loading={<Skeleton height='530px' />}
          style={isComparisonMode ? { gridColumn: 'span 6' } : { gridColumn: 'span 3' }}
        >
          <Suspense fallback={<Skeleton height='530px' />}>
            <AgentReportHistogram
              agents={agents}
              startDate={dateRange.startDate ?? ''}
              endDate={dateRange.endDate ?? ''}
            />
          </Suspense>
        </VisibleRender>

        <VisibleRender
          dependencies={dependeciesAgent}
          loading={<Skeleton height='480px' />}
          style={{ gridColumn: 'span 3' }}
        >
          <Suspense fallback={<Skeleton height='480px' />}>
            <AgentReportEconomicActivityList
              title='Principais CNAEs atendidos'
              type='cnae'
              agents={agents}
              startDate={dateRange.startDate ?? ''}
              endDate={dateRange.endDate ?? ''}
            />
          </Suspense>
        </VisibleRender>

        <VisibleRender
          dependencies={dependeciesAgent}
          loading={<Skeleton height='480px' />}
          style={{ gridColumn: 'span 3' }}
        >
          <Suspense fallback={<Skeleton height='480px' />}>
            <AgentReportEconomicActivityList
              title='Principais segmentos atendidos'
              type='segment'
              agents={agents}
              startDate={dateRange.startDate ?? ''}
              endDate={dateRange.endDate ?? ''}
            />
          </Suspense>
        </VisibleRender>

        <VisibleRender
          dependencies={dependeciesAgent}
          loading={<Skeleton height='480px' />}
          style={{ gridColumn: 'span 6' }}
        >
          <Suspense fallback={<Skeleton height='480px' />}>
            <AgentReportIsBranch
              title='Atendimentos por matriz ou filial'
              validAttendances={
                isComparisonMode && comparisonAgent
                  ? [getAttendances(mainAgent), getAttendances(comparisonAgent)]
                  : [getAttendances(mainAgent)]
              }
              isLoading={
                !!getLoading(mainAgent) || !!(comparisonAgent && !!getLoading(comparisonAgent))
              }
            />
          </Suspense>
        </VisibleRender>
      </GridContainer>
    </MapCenterProvider>
  )
}

export default memo(AgentReportContent, (prevProps, nextProps) => {
  return (
    isEqual(prevProps.agent, nextProps.agent) &&
    isEqual(prevProps.dateRange, nextProps.dateRange) &&
    prevProps.google === nextProps.google &&
    prevProps.apiKey === nextProps.apiKey &&
    isEqual(prevProps.validAttendances, nextProps.validAttendances) &&
    isEqual(prevProps.isLoadingAttendances, nextProps.isLoadingAttendances)
  )
})
